import React, { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Clock,
  Sun,
  Moon,
} from "lucide-react";

const ElegantContactPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scrollPosition, setScrollPosition] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const heroRef = useRef(null);

  // Track time for unique animated elements
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    // Check for system preference on initial load
    // if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   setDarkMode(true);
    // }

    // Listen for changes in system preferences
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      // setDarkMode(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      clearInterval(timer);
      window.removeEventListener("scroll", handleScroll);
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // Format time for display
  const hours = currentTime.getHours();
  const minutes = currentTime.getMinutes().toString().padStart(2, "0");
  const seconds = currentTime.getSeconds().toString().padStart(2, "0");

  // Calculate angles for clock-inspired animations
  const secondsAngle = (seconds / 60) * 360;
  const minutesAngle = ((minutes + seconds / 60) / 60) * 360;
  const hoursAngle = (((hours % 12) + minutes / 60) / 12) * 360;

  // Toggle dark mode
  // const toggleDarkMode = () => {
  //   setDarkMode(!darkMode);
  // };

  // Animation on scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const reveals = document.querySelectorAll(".reveal");

      reveals.forEach((element) => {
        const windowHeight = window.innerHeight;
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;

        if (elementTop < windowHeight - elementVisible) {
          element.classList.add("active");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    // Trigger once on load
    setTimeout(handleScroll, 300);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("https://apigulmohar.dodunsoftsolutions.com/api/contact-form", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          company: "Dev Bhoomi Group Companies",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        alert("Message sent successfully!");
      } else {
        alert(
          `Error: ${data.message || "Something went wrong. Please try again."}`
        );
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      alert("Failed to send message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`transition-colors duration-300`}>
      {/* Theme Toggle Button */}
      {/* <button 
        onClick={toggleDarkMode} 
        className="fixed top-4 right-4 z-50 p-2 rounded-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-lg transition-colors duration-300"
        title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
      >
        {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button> */}

      {/* Hero Section - With full light/dark theme support */}
      <section
        ref={heroRef}
        className="relative min-h-screen overflow-hidden bg-gradient-to-br from-white to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-500"
      >
        {/* Unique layered background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Asymmetric shapes - Light/Dark variants */}
          <div
            className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-400/10 dark:bg-gradient-to-br dark:from-yellow-900/30 dark:to-yellow-700/20 "
            style={{
              transform: `translate(${scrollPosition * 0.05}px, ${scrollPosition * 0.02
                }px)`,
              transition: "transform 0.3s ease-out, background-color 0.5s ease",
              opacity: isLoaded ? 0.6 : 0,
            }}
          ></div>

          <div
            className="absolute -bottom-48 -right-48 h-96 w-96 rounded-full bg-gradient-to-tl from-yellow-500/20 to-yellow-400/10 dark:bg-gradient-to-tl dark:from-amber-900/30 dark:to-amber-700/20"
            style={{
              transform: `translate(${scrollPosition * -0.05}px, ${scrollPosition * -0.02
                }px)`,
              transition: "transform 0.3s ease-out, background-color 0.5s ease",
              opacity: isLoaded ? 0.6 : 0,
            }}
          ></div>

          {/* Circular timeline inspired by watch face */}
          <div
            className={`absolute left-1/2 top-1/2 h-[130vw] w-[130vw] max-w-[1200px] max-h-[1200px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-800/10 dark:border-white/10 transition-colors duration-500`}
          >
            {/* Hour markings - 12 divisions */}
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className={`absolute left-1/2 top-1/2 h-full w-[1px] -translate-x-1/2 -translate-y-1/2 bg-gray-800/10 dark:bg-white/10 transition-colors duration-500`}
                style={{
                  transform: `translate(-50%, -50%) rotate(${i * 30}deg)`,
                }}
              ></div>
            ))}

            {/* Animated clock hands for unique movement */}
            <div
              className="absolute left-1/2 top-1/2 h-[30%] w-[1px] origin-bottom -translate-x-1/2 bg-gradient-to-t from-yellow-500/80 to-transparent dark:from-yellow-500/90"
              style={{
                transform: `translate(-50%, 0) rotate(${hoursAngle}deg)`,
                opacity: isLoaded ? 0.4 : 0,
                transition: "opacity 1s ease-out",
              }}
            ></div>

            <div
              className="absolute left-1/2 top-1/2 h-[40%] w-[1px] origin-bottom -translate-x-1/2 bg-gradient-to-t from-amber-500/80 to-transparent dark:from-amber-500/90"
              style={{
                transform: `translate(-50%, 0) rotate(${minutesAngle}deg)`,
                opacity: isLoaded ? 0.3 : 0,
                transition: "opacity 1s ease-out",
              }}
            ></div>

            <div
              className="absolute left-1/2 top-1/2 h-[45%] w-[1px] origin-bottom -translate-x-1/2 bg-gradient-to-t from-yellow-500/70 to-transparent dark:from-yellow-500/80"
              style={{
                transform: `translate(-50%, 0) rotate(${secondsAngle}deg)`,
                opacity: isLoaded ? 0.2 : 0,
                transition: "opacity 1s ease-out",
              }}
            ></div>
          </div>
        </div>

        {/* Content wrapper */}
        <div className="relative z-10 mx-auto grid min-h-screen items-center px-6 py-24 sm:px-10 lg:container lg:px-16 xl:px-24">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-8 xl:gap-16">
            {/* Left content column */}
            <div
              className="relative"
              style={{
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? "translateY(0)" : "translateY(30px)",
                transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
              }}
            >
              {/* Unique premium badge with time display */}
              <div
                className={`inline-flex items-center gap-3 rounded-full border border-gray-900/10 bg-white/80 backdrop-blur-md dark:border dark:border-white/10 dark:bg-black/30 dark:backdrop-blur-md px-4 py-2 transition-colors duration-500`}
              >
                <span
                  className={`text-xs font-medium uppercase tracking-widest text-gray-800 dark:text-white/80 transition-colors duration-500`}
                >
                  Get In Touch
                </span>
              </div>

              {/* Premium heading with unique typography */}
              <h1
                className={`mt-8 font-serif text-5xl font-light leading-tight tracking-tight text-gray-900 dark:text-white xl:text-6xl transition-colors duration-500`}
              >
                <span
                  className="block"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                    transition:
                      "opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s",
                  }}
                >
                  Connect With Us
                </span>
                <span className="mt-2 block text-transparent">
                  We're Here For You
                </span>
              </h1>

              {/* Elegant description */}
              <p
                className={`mt-8 max-w-xl text-lg font-light leading-relaxed text-gray-700 dark:text-gray-200 transition-colors duration-500`}
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                  transition:
                    "opacity 1s ease-out 0.6s, transform 1s ease-out 0.6s",
                }}
              >
                Have questions or looking to partner with Dev Bhoomi Group Companies?
                We're committed to providing exceptional service and support.
                Reach out to our dedicated team today.
              </p>

              {/* Unique aesthetic CTA buttons */}
              <div
                className="mt-12 flex flex-wrap items-center gap-6"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                  transition:
                    "opacity 1s ease-out 0.8s, transform 1s ease-out 0.8s",
                }}
              >
                <a
                  href="#contact-form"
                  className={`group relative overflow-hidden rounded-sm bg-white dark:bg-gray-800 px-8 py-4 transition-colors duration-500`}
                >
                  <span
                    className={`relative z-10 font-medium text-gray-900 dark:text-white transition-colors duration-500`}
                  >
                    Send Message
                  </span>
                  <ChevronRight
                    className={`relative z-10 ml-2 inline h-5 w-5 text-gray-900 dark:text-white transition-colors duration-500`}
                  />

                  {/* Unique hover reveal */}
                  <span className="absolute inset-0 z-0 bg-gradient-to-r from-amber-400 to-amber-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
                  <span
                    className={`absolute inset-0 z-0 bg-white border-amber-100 border-2 dark:border dark:bg-gray-800 transition-transform duration-500 group-hover:-translate-y-full`}
                  ></span>
                </a>

                <a
                  href="#map-section"
                  className={`group flex items-center text-gray-900 dark:text-white transition duration-300`}
                >
                  <span className="relative">
                    Visit Our Office
                    <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-gradient-to-r from-yellow-500 to-amber-500 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>

              {/* Contact quick stats */}
              <div
                className="mt-16 grid grid-cols-2 gap-x-12 gap-y-8 sm:grid-cols-4"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                  transition:
                    "opacity 1s ease-out 1s, transform 1s ease-out 1s",
                }}
              >
                <div className="relative">
                  <div
                    className={`flex items-center text-lg text-gray-900 dark:text-white transition-colors duration-500`}
                  >
                    <Mail className="h-5 w-5 mr-2 text-yellow-400" /> Email
                  </div>
                  <div
                    className={`mt-1 text-sm text-gray-600 dark:text-gray-300 transition-colors duration-500`}
                  >
                    24hr Response
                  </div>
                  <div className="absolute -left-3 top-0 h-full w-[1px] bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                </div>

                <div className="relative">
                  <div
                    className={`flex items-center text-lg text-gray-900 dark:text-white transition-colors duration-500`}
                  >
                    <Phone className="h-5 w-5 mr-2 text-amber-400" /> Call
                  </div>
                  <div
                    className={`mt-1 text-sm text-gray-600 dark:text-gray-300 transition-colors duration-500`}
                  >
                    9am-6pm IST
                  </div>
                  <div className="absolute -left-3 top-0 h-full w-[1px] bg-gradient-to-b from-amber-500/50 to-transparent"></div>
                </div>

                <div className="relative">
                  <div
                    className={`flex items-center text-lg text-gray-900 dark:text-white transition-colors duration-500`}
                  >
                    <MapPin className="h-5 w-5 mr-2 text-yellow-400" /> Visit
                  </div>
                  <div
                    className={`mt-1 text-sm text-gray-600 dark:text-gray-300 transition-colors duration-500`}
                  >
                    Una, HP
                  </div>
                  <div className="absolute -left-3 top-0 h-full w-[1px] bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                </div>

                <div className="relative">
                  <div
                    className={`flex items-center text-lg text-gray-900 dark:text-white transition-colors duration-500`}
                  >
                    <Clock className="h-5 w-5 mr-2 text-yellow-400" /> Response
                  </div>
                  <div
                    className={`mt-1 text-sm text-gray-600 dark:text-gray-300 transition-colors duration-500`}
                  >
                    Within 24hrs
                  </div>
                  <div className="absolute -left-3 top-0 h-full w-[1px] bg-gradient-to-b from-amber-500/50 to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Right content column - unique premium card */}
            <div
              className="relative hidden lg:block"
              style={{
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? "translateX(0)" : "translateX(30px)",
                transition:
                  "opacity 1s ease-out 0.4s, transform 1s ease-out 0.4s",
              }}
            >
              {/* Unique premium card with layered design */}
              <div className="relative">
                {/* Layered card elements */}
                <div className="absolute -right-4 -top-4 h-full w-full border border-white/10 bg-white/5 dark:bg-black/30 backdrop-blur-sm"></div>
                <div className="absolute -left-4 -bottom-4 h-full w-full border border-white/10 bg-black/40 dark:bg-black/60 backdrop-blur-sm"></div>

                {/* Main card */}
                <div className="relative border border-white/20 bg-gradient-to-br from-black/80 to-[#0c0c0c]/90 dark:from-gray-900 dark:to-black p-8 backdrop-blur-md">
                  {/* Unique image display with overlapping elements */}
                  <div className="relative mb-8">
                    <div className="absolute -left-4 -top-4 h-full w-full border border-white/10 overflow-hidden">
                      <div
                        className="h-full w-full bg-gradient-to-r from-yellow-900/20 to-amber-900/20 dark:from-yellow-900/30 dark:to-amber-900/30"
                        style={{
                          opacity: isLoaded ? 0.5 : 0,
                          transition: "opacity 1s ease-out",
                        }}
                      ></div>
                    </div>

                    <div className="relative overflow-hidden">
                      <img
                        src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=600"
                        alt="Contact team"
                        className="w-full transform object-cover transition duration-700 hover:scale-105"
                      />
                    </div>

                    {/* Unique caption overlay */}
                    <div
                      className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent dark:from-black"
                      style={{
                        opacity: isLoaded ? 1 : 0,
                        transform: isLoaded
                          ? "translateY(0)"
                          : "translateY(10px)",
                        transition:
                          "opacity 1s ease-out 0.8s, transform 1s ease-out 0.8s",
                      }}
                    >
                      <h3 className="flex items-center font-serif text-white p-4">
                        <span className="mr-2 h-[1px] w-6 bg-yellow-400"></span>
                        Our Dedicated Support Team
                      </h3>
                    </div>
                  </div>

                  {/* Unique testimonial presentation */}
                  <div
                    className="relative mb-8 py-4"
                    style={{
                      opacity: isLoaded ? 1 : 0,
                      transition: "opacity 1s ease-out 1s",
                    }}
                  >
                    <div className="absolute -left-4 top-0 h-12 w-[1px] bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                    <div className="absolute -right-4 bottom-0 h-12 w-[1px] bg-gradient-to-t from-amber-500/50 to-transparent"></div>

                    <blockquote className="font-serif text-lg italic text-gray-300 dark:text-gray-200">
                      "The customer service at Dev Bhoomi Group Companies is exceptional.
                      Their team responded promptly and resolved our concerns
                      professionally."
                    </blockquote>
                  </div>

                  {/* Contact methods */}
                  {/* <div 
                    className="space-y-6"
                    style={{
                      opacity: isLoaded ? 1 : 0,
                      transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                      transition: 'opacity 1s ease-out 1.2s, transform 1s ease-out 1.2s'
                    }}
                  >
                    <div className="relative pl-6">
                      <div className="absolute left-0 top-0 h-full w-[1px] bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                      <h4 className="text-sm font-medium uppercase tracking-wider text-white">Email Support</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-400 dark:text-gray-300">
                        info@devbhoomigroup.in
                      </p>
                    </div>
                    
                    <div className="relative pl-6">
                      <div className="absolute left-0 top-0 h-full w-[1px] bg-gradient-to-b from-amber-500/50 to-transparent"></div>
                      <h4 className="text-sm font-medium uppercase tracking-wider text-white">Phone Support</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-400 dark:text-gray-300">
                        +91 1234 567 890
                      </p>
                    </div>
                    
                    <div className="relative pl-6">
                      <div className="absolute left-0 top-0 h-full w-[1px] bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                      <h4 className="text-sm font-medium uppercase tracking-wider text-white">Business Hours</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-400 dark:text-gray-300">
                        Monday - Friday: 9:00 AM - 6:00 PM<br/>
                        Saturday: 10:00 AM - 4:00 PM
                      </p>
                    </div>
                  </div> */}

                  {/* Department badges */}
                  <div
                    className="mt-8 flex flex-wrap gap-2"
                    style={{
                      opacity: isLoaded ? 1 : 0,
                      transition: "opacity 1s ease-out 1.4s",
                    }}
                  >
                    <span className="rounded-sm border border-yellow-500/30 bg-yellow-500/10 px-3 py-1 text-xs text-yellow-400">
                      Sales
                    </span>
                    <span className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-400">
                      Support
                    </span>
                    <span className="rounded-sm border border-yellow-500/30 bg-yellow-500/10 px-3 py-1 text-xs text-yellow-400">
                      Careers
                    </span>
                    <span className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-400">
                      Partnerships
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Unique bottom decoration with light/dark variants */}
        <div
          className={`absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gray-800/20 to-transparent dark:via-white/20 dark:to-transparent transition-colors duration-500`}
        ></div>
      </section>

      {/* Main Content */}
      <main
        id="main-content"
        className="py-24 bg-white dark:bg-gray-900 transition-colors duration-300"
      >
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Title */}
          <div className="text-center max-w-3xl mx-auto mb-20 reveal fade-up">
            <span className="inline-block px-4 py-1 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100 rounded-full text-sm font-medium mb-4 transition-colors duration-300">
              Contact Us
            </span>
            <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6 text-gray-900 dark:text-white transition-colors duration-300">
              Get In Touch With Our Team
            </h2>
            <div className="w-16 h-px bg-yellow-400 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-300 mt-6 text-lg transition-colors duration-300">
              We're here to answer your questions and discuss how Dev Bhoomi
              Group Companies can help you achieve your goals.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="md:col-span-3 reveal fade-right">
              <div
                className="bg-white dark:bg-gray-800 p-10 shadow-xl border border-gray-100 dark:border-gray-700 transition-colors duration-300"
                id="contact-form"
              >
                <h3 className="text-2xl font-light mb-8 text-gray-900 dark:text-white transition-colors duration-300">
                  Send Us a Message
                </h3>

                {submitted ? (
                  <div className="flex items-center p-6 bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 transition-colors duration-300">
                    <div className="text-green-500 mr-4">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-green-800 dark:text-green-200 text-lg transition-colors duration-300">
                        Thank you for your message
                      </h4>
                      <p className="text-green-700 dark:text-green-300 mt-1 transition-colors duration-300">
                        We will get back to you as soon as possible.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300"
                        >
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full border-b border-gray-300 dark:border-gray-600 py-3 focus:border-yellow-500 focus:outline-none bg-transparent text-gray-900 dark:text-white transition-colors duration-300"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300"
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full border-b border-gray-300 dark:border-gray-600 py-3 focus:border-yellow-500 focus:outline-none bg-transparent text-gray-900 dark:text-white transition-colors duration-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300"
                        >
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full border-b border-gray-300 dark:border-gray-600 py-3 focus:border-yellow-500 focus:outline-none bg-transparent text-gray-900 dark:text-white transition-colors duration-300"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="subject"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300"
                        >
                          Subject
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full border-b border-gray-300 dark:border-gray-600 py-3 focus:border-yellow-500 focus:outline-none bg-transparent text-gray-900 dark:text-white transition-colors duration-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300"
                      >
                        Your Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="6"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="w-full border-b border-gray-300 dark:border-gray-600 py-3 focus:border-yellow-500 focus:outline-none bg-transparent text-gray-900 dark:text-white transition-colors duration-300 resize-none"
                      ></textarea>
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full ${loading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-amber-500 hover:bg-amber-600"
                          } text-white dark:text-black font-bold py-3 px-4 rounded-md transition-all duration-300 flex items-center justify-center`}
                      >
                        {loading ? "Sending..." : "Send Message"}
                        {!loading && <ArrowRight className="ml-2 h-5 w-5" />}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="md:col-span-2 reveal fade-left">
              <div className="bg-gray-50 dark:bg-gray-800 h-full p-10 transition-colors duration-300">
                <h3 className="text-2xl font-light mb-8 text-gray-900 dark:text-white transition-colors duration-300">
                  Our Office
                </h3>

                <div className="mb-10">
                  <h4 className="text-lg font-medium mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                    Dev Bhoomi Group Companies HQ
                  </h4>
                  <div className="space-y-4 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                    <p className="flex items-start">
                      <svg
                        className="w-5 h-5 mr-3 text-yellow-500 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>
                        Dev Bhoomi Group Companies,
                        <br />
                        Admin Block at The Gulmohar Grand,
                        <br />
                        8 mile stone, Una Chandigarh
                        <br />
                        Highway Behdala,Una,
                        <br />
                        Himachal Pradesh 174306
                      </span>
                    </p>
                    <p className="flex items-start">
                      <svg
                        className="w-5 h-5 mr-3 text-yellow-500 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <a
                        href="mailto:info@devbhoomigroup.in"
                        className=" hover:text-amber-400 transition-colors"
                      >
                        info@devbhoomigroup.in
                      </a>
                    </p>
                    <p className="flex items-start">
                      <svg
                        className="w-5 h-5 mr-3 text-yellow-500 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <a
                        href="tel:+916230562314"
                        className=" hover:text-amber-400 transition-colors"
                      >
                        +91 6230562314
                      </a>

                    </p>
                  </div>
                </div>

                <div className="mb-10">
                  <h4 className="text-lg font-medium mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                    Business Hours
                  </h4>
                  <table className="w-full text-gray-600 dark:text-gray-300 transition-colors duration-300">
                    <tbody>
                      <tr className="border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                        <td className="py-3">Monday - Friday</td>
                        <td className="py-3 text-right">9:00 AM - 6:00 PM</td>
                      </tr>
                      <tr className="border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                        <td className="py-3">Saturday</td>
                        <td className="py-3 text-right">10:00 AM - 4:00 PM</td>
                      </tr>
                      <tr>
                        <td className="py-3">Sunday</td>
                        <td className="py-3 text-right">Closed</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Map Section */}
      <section
        id="map-section"
        className="py-24 bg-gray-50 dark:bg-gray-800 transition-colors duration-300"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 reveal fade-up">
            <span className="inline-block px-4 py-1 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100 rounded-full text-sm font-medium mb-4 transition-colors duration-300">
              Location
            </span>
            <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6 text-gray-900 dark:text-white transition-colors duration-300">
              Visit Our Office
            </h2>
            <div className="w-16 h-px bg-yellow-400 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-12 items-center">
            <div className="md:col-span-2 h-96 bg-white dark:bg-gray-700 shadow-xl reveal fade-right transition-colors duration-300">
              {/* Embedded Google Map */}
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d32723.697734887915!2d76.32583631041322!3d31.427298223999504!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391acf7372e92127%3A0x190e1127f9bcee3!2sDev%20Bhoomi%20Group%20companies!5e0!3m2!1sen!2sin!4v1745478025319!5m2!1sen!2sin"
                className="w-full h-full border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            <div className="reveal fade-left">
              <h3 className="text-2xl font-light mb-6 text-gray-900 dark:text-white transition-colors duration-300">
                Directions
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed transition-colors duration-300">
                Our headquarters is located at the 8 mile stone in Behdala, Una.
                The location is well-connected to major highways and easily
                accessible from neighboring cities.
              </p>
              <ul className="space-y-4 text-gray-600 dark:text-gray-300 mb-8 transition-colors duration-300">
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-sm mr-3 mt-0.5 transition-colors duration-300">
                    1
                  </span>
                  <span>Follow National Highway towards Una city center</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-sm mr-3 mt-0.5 transition-colors duration-300">
                    2
                  </span>
                  <span>
                    Continue for approximately 5 km past the city center
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-sm mr-3 mt-0.5 transition-colors duration-300">
                    3
                  </span>
                  <span>
                    Look for the Dev Bhoomi Group Companies sign at the 8 mile stone
                  </span>
                </li>
              </ul>
              <a
                href="https://maps.google.com/?q=Dev+Bhoomi+Group+HQ,+Una,+Himachal+Pradesh"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 font-medium transition-colors duration-300"
              >
                <span>Get directions</span>
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Dark Mode Style Tag for additional CSS */}
      <style jsx global>{`
        .dark .reveal.active {
          opacity: 1;
          transform: translateY(0) !important;
        }

        /* Custom CSS for dark mode transitions */
        .dark .fade-up {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .dark .fade-right {
          opacity: 0;
          transform: translateX(-20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .dark .fade-left {
          opacity: 0;
          transform: translateX(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};
export default ElegantContactPage;
